import { useMemo, useState } from 'react';
import React from 'react';
import styled from 'styled-components';

import DeleteSummaryModal from 'components/modals/DeleteSummaryModal';
import EditPatientInformationModal from 'components/modals/EditPatientInformationModal';
import EditSummaryTitleModal from 'components/modals/EditSummaryTitleModal';
import Button from 'components/shared/Button';
import DividerSeries from 'components/shared/DividerSeries';
import Menu from 'components/shared/menu';
import Summarization from 'models/Summarization';
import colors from 'styles/theme/colors';
import EllipsisIcon from 'svg/EllipsisIcon';
import PencilIcon from 'svg/PencilIcon';
import PhysicianIcon from 'svg/PhysicianIcon';
import ThreeDotMenu from 'svg/ThreeDotMenu';
import TrashIcon from 'svg/TrashIcon';

interface Props {
  summarization: Summarization;
  inline?: boolean;
}

export default function SummarizationActionsMenu({ summarization, inline = false }: Props) {
  const [showEditTitleModal, setShowEditTitleModal] = useState(false);
  const [showEditPatientModal, setShowEditPatientModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const actions: {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    show?: boolean;
    color?: string;
  }[][] = useMemo(() => {
    return [
      [
        {
          icon: <PencilIcon color={'transparent'} stroke={colors.black100} strokeWidth={1.5} />,
          label: 'Edit summary title',
          onClick: () => setShowEditTitleModal(true),
          show: true,
        },
        {
          icon: <PhysicianIcon width={16} height={16} stroke={colors.black100} />,
          label: 'Edit patient information',
          onClick: () => setShowEditPatientModal(true),
          show: !!Array.from(Object.values(summarization.patient ?? {})).join(''),
        },
      ],
      [
        {
          icon: <TrashIcon width={16} height={16} />,
          label: 'Delete summary',
          onClick: () => setShowDeleteModal(true),
          color: colors.red100,
          show: true,
        },
      ],
    ];
  }, [summarization.patient]);

  const showMenu = actions.some((section) => section.some((action) => action.show));

  return (
    <>
      {showMenu && (
        <Menu $size='large'>
          <Menu.Trigger>
            {inline ? (
              <MenuButton>
                <ThreeDotMenu />
              </MenuButton>
            ) : (
              <Button
                variant='outline-gray'
                style={{ width: 44, padding: 0, height: 44, flexShrink: 0 }}
                data-cy='summarizationActionsMenuButton'>
                <EllipsisIcon />
              </Button>
            )}
          </Menu.Trigger>
          <Menu.Content position='right' style={{ marginTop: 4 }}>
            <DividerSeries divider={<Menu.Divider />}>
              {actions.map((section, index) => (
                <React.Fragment key={index}>
                  {section
                    .filter((action) => action.show)
                    .map((action, i) => (
                      <StyledMenuItem
                        data-cy='summarization-action-menu-item'
                        style={{ color: action.color }}
                        key={i}
                        onClick={action.onClick}>
                        {action.icon}
                        {action.label}
                      </StyledMenuItem>
                    ))}
                </React.Fragment>
              ))}
            </DividerSeries>
          </Menu.Content>
        </Menu>
      )}

      {showEditTitleModal && (
        <EditSummaryTitleModal summarization={summarization} onCancel={() => setShowEditTitleModal(false)} />
      )}

      {showEditPatientModal && (
        <EditPatientInformationModal summarization={summarization} onCancel={() => setShowEditPatientModal(false)} />
      )}

      {showDeleteModal && (
        <DeleteSummaryModal summarization={summarization} onCancel={() => setShowDeleteModal(false)} />
      )}
    </>
  );
}

const StyledMenuItem = styled(Menu.Item)`
  gap: 12px;
`;

const MenuButton = styled.button`
  all: unset;
  cursor: pointer;
  height: 16px;
  padding: 0 2px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
